/**
 * FilterChips Component
 *
 * Active filter display with remove functionality.
 */

import React from 'react';
import type { SearchFilters } from '../hooks/useSearchFilters';

interface FilterChipsProps {
  filters: SearchFilters;
  onChange: (filters: Partial<SearchFilters>) => void;
}

interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

export const FilterChips: React.FC<FilterChipsProps> = ({ filters, onChange }) => {
  const chips: FilterChip[] = [];

  // Price range
  if (filters.priceMin || filters.priceMax) {
    const min = filters.priceMin || 0;
    const max = filters.priceMax || 1000;
    chips.push({
      key: 'price',
      label: `$${min} - $${max}`,
      onRemove: () => onChange({ priceMin: undefined, priceMax: undefined }),
    });
  }

  // Property types
  filters.propertyTypes.forEach((type) => {
    chips.push({
      key: `type-${type}`,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      onRemove: () =>
        onChange({
          propertyTypes: filters.propertyTypes.filter((t) => t !== type),
        }),
    });
  });

  // Bedrooms
  if (filters.bedrooms) {
    chips.push({
      key: 'bedrooms',
      label: `${filters.bedrooms}+ bedrooms`,
      onRemove: () => onChange({ bedrooms: undefined }),
    });
  }

  // Bathrooms
  if (filters.bathrooms) {
    chips.push({
      key: 'bathrooms',
      label: `${filters.bathrooms}+ bathrooms`,
      onRemove: () => onChange({ bathrooms: undefined }),
    });
  }

  // Amenities
  const amenityLabels: Record<string, string> = {
    pool: 'Pool',
    wifi: 'WiFi',
    kitchen: 'Kitchen',
    ac: 'AC',
    parking: 'Parking',
    beach_access: 'Beach access',
    gym: 'Gym',
    fireplace: 'Fireplace',
    washer: 'Washer',
    dryer: 'Dryer',
    hot_tub: 'Hot tub',
    bbq: 'BBQ',
  };

  filters.amenities.forEach((amenity) => {
    chips.push({
      key: `amenity-${amenity}`,
      label: amenityLabels[amenity] || amenity,
      onRemove: () =>
        onChange({
          amenities: filters.amenities.filter((a) => a !== amenity),
        }),
    });
  });

  // Instant booking
  if (filters.instantBooking) {
    chips.push({
      key: 'instant',
      label: 'Instant Book',
      onRemove: () => onChange({ instantBooking: false }),
    });
  }

  // Pet friendly
  if (filters.petFriendly) {
    chips.push({
      key: 'pets',
      label: 'Pet friendly',
      onRemove: () => onChange({ petFriendly: false }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700"
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onRemove}
            className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
            aria-label={`Remove ${chip.label} filter`}
          >
            <CloseIcon className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}
    </div>
  );
};

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default FilterChips;
